
import { useState } from 'react';

const scenarios = [
  { label: '250 Acres', acres: 250 },
  { label: '1,000 Acres', acres: 1000 },
  { label: '5,000 Acres', acres: 5000 },
];

const years = [1, 2, 3, 4, 5];

const getOneOffCost = (acres: number, year: number) => {
  const baseCostPerAcre = acres < 250 ? 18 : acres < 1000 ? 14 : 10;
  const mobilizationFee = 1200;
  const annualCost = (baseCostPerAcre * acres) + mobilizationFee;
  return annualCost * year;
};

const getRetainerCost = (acres: number, year: number) => {
  const baseCostPerAcre = acres < 250 ? 12 : acres < 1000 ? 8 : 5.5;
  const annualCost = baseCostPerAcre * acres;
  const baselineSetup = acres < 250 ? 800 : acres < 1000 ? 1500 : 2500;
  return (annualCost * year) + baselineSetup;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

export default function RetainerSavingsChart() {
  const [activeScenario, setActiveScenario] = useState(1);
  const { acres } = scenarios[activeScenario];

  const maxCost = getOneOffCost(acres, 5);

  const savingsAtYear5 = getOneOffCost(acres, 5) - getRetainerCost(acres, 5);
  const savingsPercent = Math.round((savingsAtYear5 / getOneOffCost(acres, 5)) * 100);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-stone-900 mb-4">
            Retainer Savings vs. One-Off Inspection Costs
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            One-off inspections include mobilization fees every visit. Retainers eliminate that overhead and lock in lower per-acre rates.
          </p>
        </div>

        {/* Scenario Selector */}
        <div className="flex justify-center gap-3 mb-10">
          {scenarios.map((s, i) => (
            <button
              key={s.label}
              onClick={() => setActiveScenario(i)}
              className={`px-6 py-3 rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                activeScenario === i
                  ? 'bg-amber-600 text-white shadow-md'
                  : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Savings Highlight */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-10 text-center">
          <p className="text-sm font-semibold text-amber-700 mb-1">5-Year Savings with a Retainer</p>
          <p className="text-4xl font-bold text-amber-800">{formatCurrency(savingsAtYear5)}</p>
          <p className="text-sm text-amber-600 mt-1">{savingsPercent}% less than one-off inspections over 5 years</p>
        </div>

        {/* Bar Chart */}
        <div className="bg-stone-50 rounded-xl p-8 border border-stone-200 mb-8">
          <div className="flex items-end gap-6 md:gap-10 justify-center" style={{ height: '320px' }}>
            {years.map((year) => {
              const oneOff = getOneOffCost(acres, year);
              const retainer = getRetainerCost(acres, year);
              const oneOffHeight = (oneOff / maxCost) * 260;
              const retainerHeight = (retainer / maxCost) * 260;

              return (
                <div key={year} className="flex flex-col items-center gap-2 flex-1 max-w-[120px]">
                  <div className="flex items-end gap-2 w-full justify-center" style={{ height: '280px' }}>
                    {/* One-Off Bar */}
                    <div className="flex flex-col items-center w-10 md:w-12">
                      <span className="text-xs font-semibold text-stone-500 mb-1 whitespace-nowrap">
                        {formatCurrency(oneOff)}
                      </span>
                      <div
                        className="w-full bg-stone-400 rounded-t-md transition-all duration-500"
                        style={{ height: `${oneOffHeight}px` }}
                      ></div>
                    </div>
                    {/* Retainer Bar */}
                    <div className="flex flex-col items-center w-10 md:w-12">
                      <span className="text-xs font-semibold text-amber-700 mb-1 whitespace-nowrap">
                        {formatCurrency(retainer)}
                      </span>
                      <div
                        className="w-full bg-amber-500 rounded-t-md transition-all duration-500"
                        style={{ height: `${retainerHeight}px` }}
                      ></div>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-stone-700 mt-1">Year {year}</span>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex justify-center gap-8 mt-6 pt-6 border-t border-stone-200">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-stone-400 rounded-sm"></div>
              <span className="text-sm text-stone-600">One-Off Inspections</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-500 rounded-sm"></div>
              <span className="text-sm text-stone-600">Retainer Plan</span>
            </div>
          </div>
        </div>

        {/* Breakdown Table */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-50">
                  <th className="text-left px-6 py-4 font-semibold text-stone-900">Cost Factor</th>
                  <th className="text-right px-6 py-4 font-semibold text-stone-500">One-Off (per visit)</th>
                  <th className="text-right px-6 py-4 font-semibold text-amber-700">Retainer (annual)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-stone-100">
                  <td className="px-6 py-4 text-stone-700">Per-acre rate</td>
                  <td className="px-6 py-4 text-right text-stone-600">
                    {acres < 250 ? '$18' : acres < 1000 ? '$14' : '$10'}/acre
                  </td>
                  <td className="px-6 py-4 text-right text-amber-700 font-semibold">
                    {acres < 250 ? '$12' : acres < 1000 ? '$8' : '$5.50'}/acre
                  </td>
                </tr>
                <tr className="border-t border-stone-100">
                  <td className="px-6 py-4 text-stone-700">Mobilization fee</td>
                  <td className="px-6 py-4 text-right text-stone-600">$1,200 each visit</td>
                  <td className="px-6 py-4 text-right text-amber-700 font-semibold">Included</td>
                </tr>
                <tr className="border-t border-stone-100">
                  <td className="px-6 py-4 text-stone-700">Baseline setup</td>
                  <td className="px-6 py-4 text-right text-stone-600">N/A</td>
                  <td className="px-6 py-4 text-right text-amber-700 font-semibold">
                    {formatCurrency(acres < 250 ? 800 : acres < 1000 ? 1500 : 2500)} (one-time)
                  </td>
                </tr>
                <tr className="border-t border-stone-100">
                  <td className="px-6 py-4 text-stone-700">Change detection</td>
                  <td className="px-6 py-4 text-right text-stone-600">Not available</td>
                  <td className="px-6 py-4 text-right text-amber-700 font-semibold">Included</td>
                </tr>
                <tr className="border-t border-stone-100">
                  <td className="px-6 py-4 text-stone-700">Historical timeline</td>
                  <td className="px-6 py-4 text-right text-stone-600">Not available</td>
                  <td className="px-6 py-4 text-right text-amber-700 font-semibold">Included</td>
                </tr>
                <tr className="border-t border-stone-200 bg-stone-50">
                  <td className="px-6 py-4 font-bold text-stone-900">5-Year Total</td>
                  <td className="px-6 py-4 text-right font-bold text-stone-700">
                    {formatCurrency(getOneOffCost(acres, 5))}
                  </td>
                  <td className="px-6 py-4 text-right font-bold text-amber-700">
                    {formatCurrency(getRetainerCost(acres, 5))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-10">
          <p className="text-stone-600 mb-6">
            Retainers pay for themselves within the first year for most portfolios.
          </p>
          <div className="flex gap-4 justify-center">
            <a
              href="/portfolio-monitoring-pricing"
              className="px-8 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition whitespace-nowrap cursor-pointer"
            >
              View Retainer Pricing
            </a>
            <a
              href="/instant-quote"
              className="px-8 py-3 bg-white text-stone-900 rounded-lg font-semibold hover:bg-stone-100 transition border border-stone-300 whitespace-nowrap cursor-pointer"
            >
              Request a Quote
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
